/**
 * Shopping List Service
 * Handles all shopping list operations: CRUD, sharing, templates, collaboration
 * Standalone checklist (no budget/envelope/transaction linkage)
 * 
 * SAFETY GUARDS (Dec 2024):
 * - Service-level idempotency tracking
 * - Write validation
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  increment,
  writeBatch,
  runTransaction,
  onSnapshot
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { apiClient, itemsAPI } from '@/services/api';
import { FEATURE_FLAGS } from '@/config/featureFlags';

/**
 * Service-level write tracking to prevent duplicate writes
 * Key: `${userId}_${listId}_${normalizedItemName}`
 * Value: timestamp of last write
 */
const recentWriteCache = new Map();
const WRITE_DEDUPE_WINDOW_MS = 3000; // 3 second window

/**
 * Check if this write was recently made (service-level deduplication)
 */
const wasWriteRecentlyMade = (writeKey) => {
  const lastWrite = recentWriteCache.get(writeKey);
  if (!lastWrite) return false;
  const elapsed = Date.now() - lastWrite;
  return elapsed < WRITE_DEDUPE_WINDOW_MS;
};

/**
 * Mark a write as made
 */
const markWriteAsMade = (writeKey) => {
  recentWriteCache.set(writeKey, Date.now());
  // Cleanup old entries every 20 writes
  if (recentWriteCache.size > 50) {
    const now = Date.now();
    for (const [key, timestamp] of recentWriteCache.entries()) {
      if (now - timestamp > WRITE_DEDUPE_WINDOW_MS * 2) {
        recentWriteCache.delete(key);
      }
    }
  }
};

/**
 * LIST TEMPLATES
 * Pre-built templates for quick list creation
 * 
 * Templates retain category hints for UX but do not create expenses or envelopes.
 */
export const LIST_TEMPLATES = {
  WEEKLY_GROCERIES: {
    id: 'weekly_groceries',
    name: 'Weekly Groceries',
    icon: '🛒',
    color: '#10b981', // Matches groceries-food category color
    defaultExpenseCategoryId: 'groceries-food',
    categories: [
      { 
        name: 'Groceries & Food', 
        icon: '🛒', 
        expenseCategoryId: 'groceries-food',
        items: [
          'Apples', 'Bananas', 'Lettuce', 'Tomatoes',  // Fresh Produce
          'Milk', 'Eggs', 'Cheese', 'Yogurt',          // Dairy & Eggs
          'Chicken', 'Beef', 'Fish',                   // Protein & Meat
          'Bread', 'Rice', 'Pasta',                    // Pantry Staples
          'Water', 'Juice', 'Coffee'                   // Beverages
        ]
      }
    ]
  },
  PARTY_FOOD: {
    id: 'party_food',
    name: 'Party Food & Drinks',
    icon: '🎉',
    color: '#10b981',
    defaultExpenseCategoryId: 'groceries-food',
    categories: [
      { 
        name: 'Groceries & Food', 
        icon: '🛒', 
        expenseCategoryId: 'groceries-food',
        items: [
          'Pizza', 'Chips', 'Dips', 'Snacks',         // Snacks & Treats
          'Soda', 'Juice', 'Water',                   // Drinks
          'Cake', 'Ice Cream', 'Cookies'              // Treats
        ]
      }
    ]
  },
  MEAL_PREP: {
    id: 'meal_prep',
    name: 'Meal Prep',
    icon: '🥗',
    color: '#10b981',
    defaultExpenseCategoryId: 'groceries-food',
    categories: [
      { 
        name: 'Groceries & Food', 
        icon: '🛒', 
        expenseCategoryId: 'groceries-food',
        items: [
          'Chicken Breast', 'Ground Turkey', 'Salmon', // Proteins
          'Broccoli', 'Sweet Potatoes', 'Spinach',     // Vegetables
          'Brown Rice', 'Quinoa', 'Whole Wheat Pasta'  // Grains
        ]
      }
    ]
  },
  PERSONAL_CARE: {
    id: 'personal_care',
    name: 'Personal Care',
    icon: '🧴',
    color: '#ec4899', // Matches personal-care category color
    defaultExpenseCategoryId: 'personal-care',
    categories: [
      { 
        name: 'Personal Care', 
        icon: '🧴', 
        expenseCategoryId: 'personal-care',
        items: [
          'Toothpaste', 'Toothbrush', 'Mouthwash',    // Toiletries
          'Shampoo', 'Conditioner', 'Body Wash',      // Hair & Body
          'Soap', 'Deodorant', 'Lotion',              // Skincare
          'Razor', 'Shaving Cream'                    // Grooming
        ]
      }
    ]
  },
  BLANK: {
    id: 'blank',
    name: 'Blank List',
    icon: '📝',
    color: '#6B7280',
    defaultExpenseCategoryId: 'groceries-food',
    categories: []
  }
};

/**
 * FINLIST CONTEXT: Get expense category mapping for a template category
 * Maps UI template categories to canonical expense categories for envelope tracking.
 * 
 * @param {string} templateId - Template ID (e.g., 'weekly_groceries')
 * @param {string} categoryName - Template category name (e.g., 'Fruits & Vegetables')
 * @returns {{ expenseCategoryId: string, subcategoryId: string | null }}
 */
export function getExpenseCategoryForTemplateCategory(templateId, categoryName) {
  // Find the template
  const template = Object.values(LIST_TEMPLATES).find(t => t.id === templateId);
  if (!template) {
    return { expenseCategoryId: 'groceries-food', subcategoryId: null };
  }
  
  // Find the category within the template
  const category = template.categories.find(c => c.name === categoryName);
  if (category?.expenseCategoryId) {
    return {
      expenseCategoryId: category.expenseCategoryId,
      subcategoryId: category.subcategoryId || null
    };
  }
  
  // Fall back to template default
  return {
    expenseCategoryId: template.defaultExpenseCategoryId || 'groceries-food',
    subcategoryId: null
  };
}

/**
 * LIST STATUS
 */
export const LIST_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  ARCHIVED: 'archived'
};

/**
 * ITEM STATUS
 */
export const ITEM_STATUS = {
  PENDING: 'pending',
  PURCHASED: 'purchased',
  OUT_OF_STOCK: 'out_of_stock'
};

/**
 * Create a new shopping list
 */
export async function createShoppingList(userId, listData) {
  try {
    const listsRef = collection(db, 'users', userId, 'shoppingLists');
    
    const newList = {
      name: listData.name,
      description: listData.description || '',
      icon: listData.icon || '🛒',
      color: listData.color || '#06D6A0',
      status: LIST_STATUS.ACTIVE,
      owner: userId,
      collaborators: listData.collaborators || [],
      categories: listData.categories || [],
      templateId: listData.templateId || null,
      reminder: listData.reminder || null,
      store: listData.store || null,
      tags: listData.tags || [],
      favorite: Boolean(listData.favorite),
      itemCount: 0,
      purchasedCount: 0,
      // Unified category fields for the list
      categoryId: listData.categoryId || null,
      subcategoryId: listData.subcategoryId || null,
      customCategory: listData.customCategory || null,
      customSubcategory: listData.customSubcategory || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp()
    };

    const docRef = await addDoc(listsRef, newList);
    
    return {
      id: docRef.id,
      ...newList,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastActivityAt: new Date()
    };
  } catch (error) {
    console.error('Error creating shopping list:', error);
    throw error;
  }
}

const parseTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const deriveOwnerId = (list = {}) => (
  list.owner ||
  list.ownerId ||
  list.userId ||
  list.createdBy ||
  list.createdById ||
  null
);

const pickFirstDefined = (obj = {}, keys = []) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return undefined;
};

const sanitizeListUpdates = (updates = {}) => {
  const normalized = { ...updates };
  delete normalized.estimatedTotal;
  delete normalized.totalSpent;
  delete normalized.budget;
  return normalized;
};

const normalizeListFromApi = (list) => {
  const ownerId = deriveOwnerId(list);
  return {
    ...list,
    owner: ownerId,
    ownerId,
    createdAt: parseTimestamp(list.createdAt),
    updatedAt: parseTimestamp(list.updatedAt),
    lastActivityAt: parseTimestamp(list.lastActivityAt)
  };
};

const formatItemName = (name) => {
  if (typeof name !== 'string') {
    return '';
  }

  return name
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Create a new shopping list from a template
 */
export async function createFromTemplate(userId, templateId, options = {}) {
  const template = Object.values(LIST_TEMPLATES).find(t => t.id === templateId);

  if (!template) {
    throw new Error(`Template with id "${templateId}" not found.`);
  }

  const overrides = typeof options === 'string' ? { name: options } : (options || {});
  const listData = {
    name: overrides.name?.trim() || template.name,
    description: overrides.description?.trim() || `Based on ${template.name} template`,
    icon: overrides.icon || template.icon,
    color: overrides.color || template.color,
    templateId: template.id,
    categories: template.categories.map(c => c.name),
    store: overrides.store || null,
    tags: overrides.tags || [],
    reminder: overrides.reminder || null,
    favorite: Boolean(overrides.favorite),
    // Unified category fields passed through from options
    categoryId: overrides.categoryId || null,
    subcategoryId: overrides.subcategoryId || null,
    customCategory: overrides.customCategory || null,
    customSubcategory: overrides.customSubcategory || null
  };

  const newList = await createShoppingList(userId, listData);

  // Add items from the template with proper category mapping
  const itemPromises = [];
  template.categories.forEach((category, categoryIndex) => {
    category.items.forEach(itemName => {
      const itemData = {
        name: itemName,
        category: category.name,
        categoryIndex,
        // FINLIST CONTEXT: Use template's expense category mapping
        categoryId: category.expenseCategoryId || template.defaultExpenseCategoryId || 'groceries-food',
      };
      itemPromises.push(addItemToList(userId, newList.id, itemData));
    });
  });

  await Promise.all(itemPromises);

  // Recalculate item count
  const listRef = doc(db, 'users', userId, 'shoppingLists', newList.id);
  await updateDoc(listRef, {
    itemCount: itemPromises.length,
  });

  return newList;
}

/**
 * Duplicate an existing shopping list
 * Creates a copy with all items, incrementing the name if a duplicate already exists
 */
export async function duplicateShoppingList(userId, listId) {
  try {
    // Use backend API instead of direct Firestore access
    const { listsAPI } = await import('./api.js');
    const response = await listsAPI.duplicateList(listId);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error('Failed to duplicate list');
  } catch (error) {
    console.error('Error duplicating shopping list:', error);
    throw error;
  }
}

/**
 * Generate a unique duplicate name for a list
 * Format: "List Name (copy)", then "List Name (copy 2)", "List Name (copy 3)", etc.
 */
async function generateDuplicateName(userId, originalName) {
  try {
    // Get all existing lists to check for name conflicts
    const { lists } = await getShoppingLists(userId);
    const existingNames = lists.map(list => list.name.toLowerCase());
    
    // Check if the name already has "(copy)" or "(copy N)" pattern
    const copyWithNumberPattern = /^(.+?)\s*\(copy\s*(\d+)\)$/i;
    const copyWithoutNumberPattern = /^(.+?)\s*\(copy\)$/i;
    
    const matchWithNumber = originalName.match(copyWithNumberPattern);
    const matchWithoutNumber = originalName.match(copyWithoutNumberPattern);
    
    let baseName;
    let startNumber;
    
    if (matchWithNumber) {
      // Name already has "(copy N)" - extract base name and start from next number
      baseName = matchWithNumber[1].trim();
      startNumber = parseInt(matchWithNumber[2], 10) + 1;
    } else if (matchWithoutNumber) {
      // Name has "(copy)" without number - next should be "(copy 2)"
      baseName = matchWithoutNumber[1].trim();
      startNumber = 2;
    } else {
      // First duplicate - use "(copy)" without number
      baseName = originalName.trim();
      const firstCopyName = `${baseName} (copy)`;
      
      // Check if "(copy)" is available
      if (!existingNames.includes(firstCopyName.toLowerCase())) {
        return firstCopyName;
      }
      
      // If "(copy)" is taken, start with "(copy 2)"
      startNumber = 2;
    }
    
    // Find the next available number
    let newName = `${baseName} (copy ${startNumber})`;
    let counter = startNumber;
    
    while (existingNames.includes(newName.toLowerCase()) && counter < 100) {
      counter++;
      newName = `${baseName} (copy ${counter})`;
    }
    
    return newName;
  } catch (error) {
    console.error('Error generating duplicate name:', error);
    // Fallback to simple naming if there's an error
    return `${originalName} (copy)`;
  }
}

/**
 * Get all shopping lists for a user
 * Uses direct Firestore access - no backend API dependency
 */
export async function getShoppingLists(userId, options = {}) {
  try {
    if (!userId) throw new Error('User ID required');
    
    const listsRef = collection(db, 'users', userId, 'shoppingLists');
    let q = query(listsRef, orderBy('lastActivityAt', 'desc'));
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    if (options.favoritesOnly) {
      q = query(q, where('favorite', '==', true));
    }
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }
    
    const snapshot = await getDocs(q);
    const lists = snapshot.docs.map(doc => normalizeListFromApi({ id: doc.id, ...doc.data() }));
    
    return {
      lists,
      nextCursor: null
    };
  } catch (error) {
    console.error('Error getting shopping lists from Firestore:', error);
    throw new Error('Failed to load shopping lists');
  }
}

/**
 * Get a single shopping list with all items
 * Uses direct Firestore access - no backend API dependency
 */
export async function getShoppingListById(userId, listId) {
  try {
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    const listSnap = await getDoc(listRef);
    
    if (!listSnap.exists()) {
      return null;
    }
    
    const data = listSnap.data();
    return {
      id: listSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
      lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : null
    };
  } catch (error) {
    console.error('Error getting shopping list:', error);
    throw error;
  }
}

/**
 * Update shopping list
 */
export async function updateShoppingList(userId, listId, updates) {
  try {
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    const normalizedUpdates = sanitizeListUpdates(updates);
    
    await updateDoc(listRef, {
      ...normalizedUpdates,
      updatedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating shopping list:', error);
    throw error;
  }
}

/**
 * Delete shopping list and all its items
 * Uses direct Firestore access - no backend API dependency
 */
export async function deleteShoppingList(userId, listId) {
  try {
    if (!userId || !listId) {
      throw new Error('User ID and List ID are required');
    }
    
    // First, delete all items in the list
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const itemsSnap = await getDocs(itemsRef);
    
    // Delete each item
    const deletePromises = itemsSnap.docs.map(itemDoc => 
      deleteDoc(doc(db, 'users', userId, 'shoppingLists', listId, 'items', itemDoc.id))
    );
    await Promise.all(deletePromises);
    
    // Then delete the list itself
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await deleteDoc(listRef);
    
    console.log('[deleteShoppingList] Successfully deleted list:', listId);
    return { success: true };
  } catch (error) {
    console.error('Error deleting shopping list:', error);
    throw error;
  }
}

/**
 * Get all items for a shopping list
 * Uses direct Firestore access - no backend API dependency
 */
export async function getListItems(userId, listId) {
  try {
    console.log('[getListItems] Fetching items for:', { userId, listId });
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const q = query(itemsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    console.log('[getListItems] Found', snapshot.docs.length, 'items');
    
    const toBuy = [];
    const purchased = [];
    
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const item = {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
        boughtAt: data.boughtAt?.toDate ? data.boughtAt.toDate() : (data.boughtAt ? new Date(data.boughtAt) : null)
      };
      
      if (data.status === ITEM_STATUS.PURCHASED || data.isBought) {
        purchased.push(item);
      } else {
        toBuy.push(item);
      }
    });
    
    return {
      success: true,
      data: { toBuy, purchased }
    };
  } catch (error) {
    console.error('Error getting list items:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Clear all purchased items from a shopping list
 * Uses direct Firestore access - no backend API dependency
 */
export async function clearPurchasedItems(userId, listId) {
  try {
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const purchasedQuery = query(itemsRef, where('status', '==', ITEM_STATUS.PURCHASED));
    const boughtFlagQuery = query(itemsRef, where('isBought', '==', true));
    const [purchasedSnap, boughtSnap] = await Promise.all([
      getDocs(purchasedQuery),
      getDocs(boughtFlagQuery)
    ]);
    
    const batch = writeBatch(db);
    const seen = new Set();
    let deletedCount = 0;
    
    [...purchasedSnap.docs, ...boughtSnap.docs].forEach(docSnap => {
      if (seen.has(docSnap.id)) return;
      seen.add(docSnap.id);
      batch.delete(docSnap.ref);
      deletedCount++;
    });
    
    if (deletedCount > 0) {
      await batch.commit();
      
      // Update list item count
      const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
      await updateDoc(listRef, {
        itemCount: increment(-deletedCount),
        lastActivityAt: serverTimestamp()
      });
    }
    
    return {
      success: true,
      data: { deletedCount }
    };
  } catch (error) {
    console.error('Error clearing purchased items:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Clear all items from a shopping list
 * Uses direct Firestore access - no backend API dependency
 */
export async function clearAllItems(userId, listId) {
  try {
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const snapshot = await getDocs(itemsRef);
    
    const batch = writeBatch(db);
    let deletedCount = 0;
    
    snapshot.docs.forEach(docSnap => {
      batch.delete(docSnap.ref);
      deletedCount++;
    });
    
    if (deletedCount > 0) {
      await batch.commit();
      
      // Reset list item count
      const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
      await updateDoc(listRef, {
        itemCount: 0,
        lastActivityAt: serverTimestamp()
      });
    }
    
    return {
      success: true,
      data: { deletedCount }
    };
  } catch (error) {
    console.error('Error clearing all items:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Add item to shopping list
 * 
 * SAFETY GUARDS:
 * - Service-level deduplication (3 second window)
 * - Validation of required fields
 * - Feature flag controlled
 */
export async function addItemToList(userId, listId, itemData) {
  // Validate required inputs
  if (!userId || !listId || !itemData?.name?.trim()) {
    console.error('[addItemToList] Missing required fields:', { userId: !!userId, listId: !!listId, name: !!itemData?.name });
    throw new Error('Missing required fields for adding item');
  }
  
  const normalizedName = (itemData.name || '').toString().trim().toLowerCase();
  
  // SAFETY: Service-level deduplication check
  if (FEATURE_FLAGS.ENABLE_IDEMPOTENT_WRITES) {
    const writeKey = `${userId}_${listId}_${normalizedName}`;
    
    if (wasWriteRecentlyMade(writeKey)) {
      if (import.meta?.env?.DEV) {
        console.log('[addItemToList] Blocked duplicate write:', normalizedName);
      }
      // Return null to indicate skipped write (not an error)
      return null;
    }
    
    // Mark this write immediately to prevent concurrent writes
    markWriteAsMade(writeKey);
  }
  
  try {
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    
    const newItem = {
      name: formatItemName(itemData.name),
      quantity: itemData.quantity || 1,
      unit: itemData.unit || 'piece',
      // Unified category system fields
      categoryId: itemData.categoryId || null,
      subcategoryId: itemData.subcategoryId || null,
      customCategory: itemData.customCategory || null,
      customSubcategory: itemData.customSubcategory || null,
      // Legacy field for backward compatibility
      category: itemData.categoryId || itemData.category || 'Other',
      categoryIndex: itemData.categoryIndex || 999,
      notes: itemData.notes || '',
      status: ITEM_STATUS.PENDING,
      addedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(itemsRef, newItem);
    
    // Update list item count
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await updateDoc(listRef, {
      itemCount: increment(1),
      lastActivityAt: serverTimestamp()
    });
    
    return {
      id: docRef.id,
      ...newItem,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error adding item:', error);
    throw error;
  }
}

/**
 * Update item in shopping list
 */
export async function updateListItem(userId, listId, itemId, updates) {
  try {
    const itemRef = doc(db, 'users', userId, 'shoppingLists', listId, 'items', itemId);
    
    await updateDoc(itemRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    // Update list activity
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await updateDoc(listRef, {
      lastActivityAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating item:', error);
    throw error;
  }
}

/**
 * Toggle item purchased status (standalone, no expense creation)
 * Uses Firestore transaction for atomic read-modify-write to prevent race conditions
 */
export async function toggleItemPurchased(userId, listId, itemId, options = {}) {
  const itemRef = doc(db, 'users', userId, 'shoppingLists', listId, 'items', itemId);
  const listRef = doc(db, 'users', userId, 'shoppingLists', listId);

  try {
    const result = await runTransaction(db, async (tx) => {
      // Read current item state within transaction
      const itemSnap = await tx.get(itemRef);
      if (!itemSnap.exists()) throw new Error('Item not found');

      const current = itemSnap.data();
      const wasBought = current.isBought || current.status === ITEM_STATUS.PURCHASED;
      
      // Determine new state: if markAsBought is specified, use it; otherwise toggle
      const markAsBought = options.markAsBought !== undefined ? !!options.markAsBought : !wasBought;
      const newStatus = markAsBought ? ITEM_STATUS.PURCHASED : ITEM_STATUS.PENDING;

      // Calculate purchasedCount delta
      const delta = markAsBought === wasBought ? 0 : (markAsBought ? 1 : -1);

      // Update item within transaction
      tx.update(itemRef, {
        status: newStatus,
        isBought: markAsBought,
        boughtAt: markAsBought ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });

      // Update list purchasedCount within same transaction
      if (delta !== 0) {
        tx.update(listRef, {
          purchasedCount: increment(delta),
          lastActivityAt: serverTimestamp()
        });
      } else {
        tx.update(listRef, { lastActivityAt: serverTimestamp() });
      }

      if (import.meta?.env?.DEV) {
        console.log('[toggleItemPurchased] Transaction success:', { itemId, wasBought, markAsBought, delta });
      }

      return {
        status: newStatus,
        item: { id: itemId, ...current, status: newStatus, isBought: markAsBought },
        transactionCreated: false,
        autoCompleted: false
      };
    });

    return result;
  } catch (error) {
    console.error('Error toggling item:', error);
    throw error;
  }
}

/**
 * Delete item from list
 * Uses direct Firestore access - no backend API dependency
 */
export async function deleteListItem(userId, listId, itemId) {
  try {
    console.log('[deleteListItem] Deleting item:', { userId, listId, itemId });
    
    // Direct Firestore deletion
    const itemRef = doc(db, 'users', userId, 'shoppingLists', listId, 'items', itemId);
    await deleteDoc(itemRef);
    
    // Update list item count
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await updateDoc(listRef, {
      itemCount: increment(-1),
      lastActivityAt: serverTimestamp()
    });
    
    console.log('[deleteListItem] Successfully deleted item:', itemId);
    
    return {
      deletedItem: { id: itemId },
      success: true
    };
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
}

/**
 * Fetch recently deleted items for a list
 */
export async function getRecentlyDeletedItems(userId, listId, options = {}) {
  try {
    const limitCount = options.limit ?? 5;
    const deletedRef = collection(db, 'users', userId, 'shoppingLists', listId, 'deletedItems');
    const deletedQuery = query(
      deletedRef,
      orderBy('deletedAt', 'desc'),
      limit(Math.max(limitCount * 2, limitCount))
    );

    const snapshot = await getDocs(deletedQuery);
    const includeRestored = options.includeRestored ?? false;

    const items = snapshot.docs
      .map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        deletedAt: docSnap.data().deletedAt?.toDate?.() || docSnap.data().deletedAt || null,
        restoredAt: docSnap.data().restoredAt?.toDate?.() || docSnap.data().restoredAt || null
      }))
      .filter(item => includeRestored ? true : !item.restored);

    return items.slice(0, limitCount);
  } catch (error) {
    console.error('Error loading recently deleted items:', error);
    throw error;
  }
}

/**
 * Fetch ALL deleted items across ALL shopping lists for a user
 * Used in Settings page to show global trash view
 */
export async function getAllDeletedItemsAcrossLists(userId, options = {}) {
  try {
    const maxItems = options.limit ?? 100;
    const allDeletedItems = [];
    
    // First, get all shopping lists for the user
    const listsRef = collection(db, 'users', userId, 'shoppingLists');
    const listsSnapshot = await getDocs(listsRef);
    
    // For each list, fetch its deleted items
    for (const listDoc of listsSnapshot.docs) {
      const listData = listDoc.data();
      const listId = listDoc.id;
      const listName = listData.name || 'Unnamed List';
      
      const deletedRef = collection(db, 'users', userId, 'shoppingLists', listId, 'deletedItems');
      const deletedQuery = query(
        deletedRef,
        orderBy('deletedAt', 'desc'),
        limit(50) // Limit per list
      );
      
      const deletedSnapshot = await getDocs(deletedQuery);
      
      deletedSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        // Skip already restored items
        if (data.restored) return;
        
        allDeletedItems.push({
          id: docSnap.id,
          listId: listId,
          listName: listName,
          ...data,
          deletedAt: data.deletedAt?.toDate?.() || data.deletedAt || null,
        });
      });
    }
    
    // Sort all items by deletedAt (newest first) and limit
    allDeletedItems.sort((a, b) => {
      const dateA = a.deletedAt ? new Date(a.deletedAt) : new Date(0);
      const dateB = b.deletedAt ? new Date(b.deletedAt) : new Date(0);
      return dateB - dateA;
    });
    
    return allDeletedItems.slice(0, maxItems);
  } catch (error) {
    console.error('Error loading all deleted items:', error);
    throw error;
  }
}

/**
 * Restore a deleted item back into the list
 */
export async function restoreDeletedItem(userId, listId, deletedItemId, options = {}) {
  try {
    const deletedRef = doc(db, 'users', userId, 'shoppingLists', listId, 'deletedItems', deletedItemId);
    const deletedSnap = await getDoc(deletedRef);

    if (!deletedSnap.exists()) {
      throw new Error('Deleted item not found');
    }

    const deletedData = deletedSnap.data();
    if (deletedData.restored) {
      throw new Error('Item already restored');
    }

    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const defaultStatus = deletedData.statusBeforeDelete && deletedData.statusBeforeDelete !== ITEM_STATUS.PURCHASED
      ? deletedData.statusBeforeDelete
      : ITEM_STATUS.PENDING;
    const statusToUse = options.restoreStatus || defaultStatus;

    const newItemData = {
      name: deletedData.name,
      quantity: deletedData.quantity || 1,
      unit: deletedData.unit || 'piece',
      category: deletedData.category || 'Other',
      categoryIndex: deletedData.categoryIndex ?? 999,
      price: deletedData.price ?? deletedData.estimatedPrice ?? null,
      estimatedPrice: deletedData.estimatedPrice ?? deletedData.price ?? null,
      notes: deletedData.notes || '',
      status: statusToUse,
      addedBy: options.addedBy || deletedData.addedBy || userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const restoredRef = await addDoc(itemsRef, newItemData);

    await updateDoc(deletedRef, {
      restored: true,
      restoredAt: serverTimestamp(),
      restoredItemId: restoredRef.id
    });

    // Backend should handle list stats updates
    // await updateListStats(userId, listId);

    return {
      id: restoredRef.id,
      ...newItemData
    };
  } catch (error) {
    console.error('Error restoring deleted item:', error);
    throw error;
  }
}

/**
 * Get shopping list statistics for the current user
 * Uses direct Firestore access - no backend API dependency
 */
export async function getShoppingListStats(userId) {
  try {
    if (!userId) {
      return { totalLists: 0, activeLists: 0, completedLists: 0, totalItems: 0 };
    }
    
    const listsRef = collection(db, 'users', userId, 'shoppingLists');
    const snapshot = await getDocs(listsRef);
    
    let totalLists = 0;
    let activeLists = 0;
    let completedLists = 0;
    let totalItems = 0;
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      totalLists++;
      if (data.status === LIST_STATUS.COMPLETED) {
        completedLists++;
      } else if (data.status === LIST_STATUS.ACTIVE) {
        activeLists++;
      }
      totalItems += data.itemCount || 0;
    });
    
    return { totalLists, activeLists, completedLists, totalItems };
  } catch (error) {
    console.error('Error getting shopping list stats:', error);
    return { totalLists: 0, activeLists: 0, completedLists: 0, totalItems: 0 };
  }
}

/**
 * Update list statistics (item count, purchased count)
 */
async function updateListStats(userId, listId) {
  try {
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const itemsSnap = await getDocs(itemsRef);
    
    let purchasedCount = 0;
    let pendingCount = 0;
    
    itemsSnap.docs.forEach(doc => {
      const item = doc.data();
      if (item.status === ITEM_STATUS.PURCHASED || item.isBought) {
        purchasedCount++;
      } else {
        pendingCount++;
      }
    });
    
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    const listSnap = await getDoc(listRef);
    const listData = listSnap.data();
    const totalItems = itemsSnap.size;
    const progress = totalItems > 0 ? Math.round((purchasedCount / totalItems) * 100) : 0;

    const updates = {
      itemCount: totalItems,
      purchasedCount,
      pendingCount,
      boughtCount: purchasedCount,
      totalCount: totalItems,
      progress,
      lastActivityAt: serverTimestamp()
    };

    // Auto-complete list when all items are purchased
    if (totalItems > 0 && purchasedCount === totalItems && listData?.status === LIST_STATUS.ACTIVE) {
      updates.status = LIST_STATUS.COMPLETED;
      updates.completedAt = serverTimestamp();
    }

    await updateDoc(listRef, updates);
    
    return { itemCount: totalItems, purchasedCount, autoCompleted: updates.status === LIST_STATUS.COMPLETED };
  } catch (error) {
    console.error('Error updating list stats:', error);
    throw error;
  }
}

export async function archiveList(userId, listId, shouldArchive = true) {
  try {
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await updateDoc(listRef, {
      status: shouldArchive ? LIST_STATUS.ARCHIVED : LIST_STATUS.ACTIVE,
      archivedAt: shouldArchive ? serverTimestamp() : null,
      lastActivityAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error archiving shopping list:', error);
    throw error;
  }
}

export async function completeList(userId, listId) {
  try {
    // Get all items in the list
    const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
    const itemsSnap = await getDocs(itemsRef);
    
    // Separate purchased and unpurchased items
    const unboughtItems = [];
    itemsSnap.docs.forEach(doc => {
      const item = doc.data();
      if (item.status !== ITEM_STATUS.PURCHASED) {
        unboughtItems.push({ id: doc.id, ...item });
      }
    });
    
    // Delete all unbought items (keep only purchased items for financial analysis)
    const deletePromises = unboughtItems.map(item => 
      deleteDoc(doc(db, 'users', userId, 'shoppingLists', listId, 'items', item.id))
    );
    await Promise.all(deletePromises);
    
    // Update list status to completed
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    await updateDoc(listRef, {
      status: LIST_STATUS.COMPLETED,
      completedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp()
    });
    
    // Backend should handle list stats updates
    // await updateListStats(userId, listId);
    
    return { 
      success: true, 
      removedItemsCount: unboughtItems.length 
    };
  } catch (error) {
    console.error('Error completing shopping list:', error);
    throw error;
  }
}

/**
 * Share list with another user
 */
export async function shareList(userId, listId, collaboratorEmail, permission = 'edit') {
  try {
    // In a real app, you'd look up the user by email
    // For now, we'll just add to collaborators array
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    
    await updateDoc(listRef, {
      collaborators: arrayUnion({
        email: collaboratorEmail,
        permission,
        addedAt: new Date()
      }),
      lastActivityAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error sharing list:', error);
    throw error;
  }
}

/**
 * Remove collaborator from list
 */
export async function removeCollaborator(userId, listId, collaboratorEmail) {
  try {
    const listRef = doc(db, 'users', userId, 'shoppingLists', listId);
    const listSnap = await getDoc(listRef);
    
    if (!listSnap.exists()) {
      throw new Error('List not found');
    }
    
    const collaborators = listSnap.data().collaborators || [];
    const updatedCollaborators = collaborators.filter(c => c.email !== collaboratorEmail);
    
    await updateDoc(listRef, {
      collaborators: updatedCollaborators,
      lastActivityAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error removing collaborator:', error);
    throw error;
  }
}

/**
 * Generates a unique name for a duplicated list.
 * If "My List (Copy)" exists, it tries "My List (Copy 2)", etc.
 */
function generateUniqueDuplicatedName(originalName) {
  const sanitized = (originalName || 'Shopping List').trim();
  const stripCopySuffix = /(?:\s*\(Copy(?: \d+)?\))+$/;
  const baseName = sanitized.replace(stripCopySuffix, '').trim() || sanitized;
  const trailingCopyMatch = sanitized.match(/\(Copy(?: (\d+))?\)$/i);

  if (!trailingCopyMatch) {
    return `${baseName} (Copy)`;
  }

  const currentCopyNumber = trailingCopyMatch[1] ? Number(trailingCopyMatch[1]) : 1;
  const nextCopyNumber = currentCopyNumber + 1;

  return `${baseName} (Copy ${nextCopyNumber})`;
}

/**
 * Refund a shopping list item (full or partial)
 * BUSINESS RULE: Only items with status "purchased" can be refunded
 * Updates item status, list totals, and creates financial transaction
 * @param {Object} params - Refund parameters
 * @param {string} params.userId - User ID
 * @param {string} params.listId - Shopping list ID
 * @param {string} params.itemId - Item ID being refunded
 * @param {number} params.refundAmount - Amount to refund (must be > 0 and <= original amount)
 * @param {number} params.refundQuantity - Quantity being refunded (optional, for partial refunds)
 * @param {string} params.refundType - 'full' or 'partial'
 * @returns {Promise<Object>} Updated item and list totals
 */
export async function refundShoppingListItem({
  userId,
  listId,
  itemId,
  refundAmount,
  refundQuantity = null,
  refundType = 'partial'
}) {
  try {
    // Validate inputs
    if (!userId || !listId || !itemId) {
      throw new Error('userId, listId, and itemId are required');
    }

    if (!refundAmount || refundAmount <= 0) {
      throw new Error('refundAmount must be greater than 0');
    }

    // CHANGED: Fetch list data for optimistic UI updates only
    const listResponse = await apiClient.get(`/lists/${listId}`);
    if (!listResponse?.data) {
      throw new Error('Shopping list not found');
    }

    const listData = listResponse.data;

    // CHANGED: Backend now handles ALL validation and Firestore operations
    // Send refund request directly - backend will validate item exists, is purchased, etc.
    const response = await itemsAPI.createRefundTransaction(itemId, {
      type: refundType, // Send the refundType directly, backend will determine full/partial
      quantity: refundQuantity,
      amount: refundAmount,
      listId
    });

    if (!response?.success) {
      throw new Error(response?.message || 'Failed to create refund');
    }

    // Backend has handled all updates - return optimistic UI data
    // Note: We don't have item details anymore, so UI should refetch or use backend response
    const currentTotalSpent = listData.totalSpent || 0;
    const currentRefundedTotal = listData.refundedTotal || 0;

    const updatedListTotals = {
      totalSpent: Math.max(0, currentTotalSpent - refundAmount),
      refundedTotal: currentRefundedTotal + refundAmount,
      purchasedCount: listData.purchasedCount,
      itemCount: listData.itemCount
    };

    return {
      success: true,
      listTotals: updatedListTotals,
      transaction: response.data
    };

  } catch (error) {
    console.error('Error refunding shopping list item:', error);
    throw error;
  }
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to shopping list items in real-time
 * FINLIST REAL-TIME: Enables instant UI updates across devices
 * 
 * @param {string} userId - User ID
 * @param {string} listId - Shopping list ID
 * @param {function} onUpdate - Callback with { toBuy: [], purchased: [] }
 * @returns {function} Unsubscribe function
 */
export function subscribeToListItems(userId, listId, onUpdate) {
  if (!userId || !listId) {
    console.warn('[SHOPPING] subscribeToListItems: Missing userId or listId');
    onUpdate({ toBuy: [], purchased: [] });
    return () => {};
  }

  const itemsRef = collection(db, 'users', userId, 'shoppingLists', listId, 'items');
  const q = query(itemsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, 
    (snapshot) => {
      const toBuy = [];
      const purchased = [];
      
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const item = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
          boughtAt: data.boughtAt?.toDate ? data.boughtAt.toDate() : (data.boughtAt ? new Date(data.boughtAt) : null),
          purchased: data.status === ITEM_STATUS.PURCHASED || data.isBought || false,
          category: data.category || data.categoryId || '',
          subcategory: data.subcategory || ''
        };
        
        if (data.status === ITEM_STATUS.PURCHASED || data.isBought) {
          purchased.push(item);
        } else {
          toBuy.push(item);
        }
      });
      
      console.log('[SHOPPING] Items subscription update:', toBuy.length, 'to buy,', purchased.length, 'purchased');
      onUpdate({ toBuy, purchased });
    },
    (error) => {
      console.error('[SHOPPING] Items subscription error:', error);
      onUpdate({ toBuy: [], purchased: [] });
    }
  );
}

/**
 * Subscribe to all shopping lists for a user
 * FINLIST REAL-TIME: Enables instant UI updates when lists are created/edited/deleted
 * 
 * @param {string} userId - User ID
 * @param {function} onUpdate - Callback with lists array
 * @returns {function} Unsubscribe function
 */
export function subscribeToShoppingLists(userId, onUpdate) {
  if (!userId) {
    console.warn('[SHOPPING] subscribeToShoppingLists: Missing userId');
    onUpdate([]);
    return () => {};
  }

  const listsRef = collection(db, 'users', userId, 'shoppingLists');
  const q = query(listsRef, where('archived', '==', false), orderBy('lastActivityAt', 'desc'));

  return onSnapshot(q, 
    (snapshot) => {
      const lists = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now()),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
          lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : null
        };
      });
      
      console.log('[SHOPPING] Lists subscription update:', lists.length, 'lists');
      onUpdate(lists);
    },
    (error) => {
      console.error('[SHOPPING] Lists subscription error:', error);
      onUpdate([]);
    }
  );
}
